
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function RondeboschPage() {
  useEffect(() => {
    document.title = 'Rondebosch Painters Cape Town | University Area Painting | Heritage Property Specialists';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services in Rondebosch, Cape Town. Expert heritage property painting, university area services, and residential painting. Quality workmanship for Rondebosch properties.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Rondebosch painters, heritage property painting, university area Cape Town');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/rondebosch`);
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Rondebosch Painters Cape Town",
      "description": "Professional painting services in Rondebosch, Cape Town",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/rondebosch`,
      "areaServed": {
        "@type": "Place",
        "name": "Rondebosch, Cape Town, Western Cape, South Africa"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.9569,
          "longitude": 18.4669
        },
        "geoRadius": "12000"
      }
    });
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Rondebosch Painters')) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/049c610bcde8eda27e2e6c776b2df648.jpg"
            alt="Professional painting services in Rondebosch, Cape Town"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Distinguished <span className="text-yellow-400">Rondebosch Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Professional painting services for Rondebosch's prestigious heritage properties and university area. Expert restoration and quality craftsmanship for distinguished homes and academic buildings.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Quote
              </a>
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Specialized Heritage Painting Services in Rondebosch
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert painting solutions for Rondebosch's unique architectural heritage, from Victorian mansions to modern university facilities, preserving character while providing modern protection.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-building-2-line',
                title: 'Heritage Property Restoration',
                description: 'Specialized restoration of Victorian and Edwardian homes using period-appropriate techniques and authentic color schemes.',
                features: ['Victorian restoration', 'Edwardian properties', 'Period colors', 'Authentic techniques']
              },
              {
                icon: 'ri-school-line',
                title: 'University & Academic',
                description: 'Professional painting for university buildings, student accommodation, and academic facilities in the Rondebosch area.',
                features: ['University buildings', 'Student accommodation', 'Academic facilities', 'Institutional painting']
              },
              {
                icon: 'ri-home-smile-line',
                title: 'Prestigious Residential',
                description: 'High-end residential painting for Rondebosch\'s distinguished homes and luxury properties.',
                features: ['Luxury homes', 'Distinguished properties', 'Premium finishes', 'Exclusive service']
              }
            ].map((service, index) => (
              <div key={index} className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <i className="ri-check-line text-green-500 mr-3"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-black mb-8">
                Why Rondebosch Properties Choose Our Heritage Expertise
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Heritage Specialization',
                    description: 'Extensive experience with Victorian and Edwardian architecture, understanding period-appropriate restoration techniques.'
                  },
                  {
                    title: 'University Area Knowledge',
                    description: 'Understanding of academic schedules and institutional requirements with flexible service timing.'
                  },
                  {
                    title: 'Prestigious Property Experience',
                    description: 'Proven track record with high-end properties requiring discretion, quality, and attention to detail.'
                  },
                  {
                    title: 'Conservation Approach',
                    description: 'Commitment to preserving architectural heritage while providing modern protection and durability.'
                  }
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                      <i className="ri-check-line text-black text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20Rondebosch%20heritage%20property%2C%20Victorian%20architecture%20restoration%2C%20university%20area%20painting%20services%2C%20prestigious%20residential%20painting%20Cape%20Town&width=600&height=700&seq=rondebosch-team-work&orientation=portrait"
                alt="Heritage painting services in Rondebosch"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              Recent Rondebosch Heritage Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our latest heritage restoration and prestigious property painting projects in Rondebosch, demonstrating our expertise in period architecture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Victorian Manor Restoration',
                location: 'Rondebosch East',
                description: 'Complete restoration of a grand Victorian manor house with authentic period color scheme and techniques.',
                image: 'https://readdy.ai/api/search-image?query=Victorian%20manor%20house%20restoration%20in%20Rondebosch%20East%20Cape%20Town%2C%20grand%20heritage%20property%20with%20period%20architecture%2C%20authentic%20Victorian%20painting%20restoration%2C%20prestigious%20residential%20property&width=400&height=300&seq=rondebosch-project-1&orientation=landscape'
              },
              {
                title: 'University Residence Hall',
                location: 'UCT Campus Area',
                description: 'Professional painting renovation of a historic university residence hall maintaining academic character.',
                image: 'https://readdy.ai/api/search-image?query=University%20residence%20hall%20painting%20in%20UCT%20campus%20area%20Rondebosch%2C%20academic%20building%20renovation%2C%20institutional%20painting%20project%2C%20university%20architecture%20Cape%20Town&width=400&height=300&seq=rondebosch-project-2&orientation=landscape'
              },
              {
                title: 'Edwardian Family Estate',
                location: 'Rondebosch Central',
                description: 'Elegant restoration of an Edwardian family estate with modern protective coatings and period authenticity.',
                image: 'https://readdy.ai/api/search-image?query=Edwardian%20family%20estate%20painting%20in%20Rondebosch%20Central%2C%20historic%20family%20home%20with%20period%20architecture%2C%20heritage%20property%20restoration%2C%20elegant%20residential%20painting%20Cape%20Town&width=400&height=300&seq=rondebosch-project-3&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-xl font-bold text-black mb-3">{project.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              What Rondebosch Clients Say
            </h2>
            <p className="text-xl text-gray-600">
              Testimonials from satisfied heritage property owners and institutions in Rondebosch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Professor Elizabeth Hartley',
                location: 'Rondebosch East',
                rating: 5,
                text: 'Exceptional restoration work on our Victorian manor. They understood the historical significance and delivered authentic results that honor the property\'s heritage.'
              },
              {
                name: 'Dr. James Wilson',
                location: 'UCT Campus Area',
                rating: 5,
                text: 'Professional service for our university residence. They worked around academic schedules and maintained the building\'s character while providing modern protection.'
              },
              {
                name: 'Margaret van der Spuy',
                location: 'Rondebosch Central',
                rating: 5,
                text: 'Beautiful work on our Edwardian family home. The attention to period detail was remarkable and the quality of workmanship exceeded our expectations.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-600 leading-relaxed mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold text-black">{testimonial.name}</div>
                  <div className="text-sm text-gray-5">{testimonial.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Preserve Your Rondebosch Heritage Property
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us for specialized heritage painting consultation and restoration services. Honor your property\'s history with expert craftsmanship and authentic techniques.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
